import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Table,
  Badge,
  Collapse,
  Input,
} from 'reactstrap';
import { SkeletonTable } from '../../../../../../../../Components/Common/Skeleton';
import { displayValue as displayParcelValue } from '../../../../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import {
  usesDyedUndyedAlpha,
  resolveDyedUndyedColumns,
  getAlphaColumnLabel,
  getProcurementProductNameId,
  getProcurementProductTypeId,
} from '../../../../../../../../shared/offer-domain/constants';
import {
  getAlphaImprovementFieldValue,
  isOfferRowImprovement,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../../../../../shared/offer-domain/improvement';
import {
  fetchProductTypePricingUnit,
  formatIncotermRoute,
  formatParcelSize,
  formatPortDateRange,
  formatPortLocation,
  isAlphaFilled,
  coalesceAlphaPrice,
} from '../../../../../../utils/deliveryDetailUtils';
import { getAuthToken } from '../../../../../../../../auth/tokenvalidation';
import useSubmittedBidder from '../../../../../../hooks/useSubmittedBidder';
import {
  collectImprovementSelections,
  collectNegotiationSelections,
  getPreselectedRowKeys,
  hasNegotiationOfferId,
  isNegotiationRowSelectable,
} from '../../../../../../../../shared/offer-domain/negotiation';
import { OperationalToleranceSectionValue } from '../../../../../../../../shared/offer-domain/ui';
import { withBidderCounter } from '../../../../../../../../shared/offer-domain/counter';
import {
  buildRequirementsFromParcel,
  deliveryDetailRowMatchesBidder,
  displayValue,
  enrichRowsPerBidder,
  mergeBidderDeliveryDetail,
  filterImprovementOfferRows,
  flattenSubmittedBidderDeliveryDetails,
  getRequirementIncotermRouteLabel,
  getOfferRowImprovementStatusLabel,
  isRowCapable,
  offerRowMatchesRequirement,
  isHpsAvailable,
} from '../../../../../../utils/tenderEvaluationHelpers';
import AlphaImprovementEditableCells from '../shared/AlphaImprovementEditableCells';
import {
  buildHpsCatalog,
  getHpsAlphaValues,
  resolveMatchingHpsKey,
  MatchingHpsBadge,
  HpsAlphaCell,
} from '../shared/hpsMatching';
import {
  OFFER_HEADER,
  OFFER_TABLE_STYLE,
  ThLabel,
  thStyle,
} from '../shared/offerTableLayout';
import {
  OfferSelectAllBar,
  OfferStatusBadges,
  OfferStatusFilter,
  filterRowsByOfferStatus,
} from '../shared/offerStatus';
import {
  enrichDeliveryItemsWithCounterOffers,
  enrichDeliveryItemsWithFobLocations,
  enrichItemsWithFobOriginParcel,
  formatFobLocationsJoined,
  getCapableOriginNamesFromFobLocations,
  isCounterDateChanged,
  isCounterQuantityChanged,
  isCounterValueChanged,
  isFobOriginDisplay,
  FobLocationLabel,
  ParcelDeliveryPricingTabs,
  renderSelectedOriginLocations,
} from '../../../../../../../../shared/offer-domain/delivery';
import {
  COUNTER_ACCENT,
  COUNTER_HIGHLIGHT_BG,
} from '../../../../../../../../shared/offer-domain/constants';
import ModalOffer from '../../ModalOffer';
import ActionButton from '../../../../../../../../Components/Common/ActionButton';

const counterValueStyle = {
  backgroundColor: COUNTER_HIGHLIGHT_BG,
  color: COUNTER_ACCENT,
  fontWeight: 600,
};

const isFobIncotermRow = data =>
  String(data?.incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getRowOriginNames = data =>
  (Array.isArray(data?.fobLocationNames) ? data.fobLocationNames : []).filter(
    Boolean
  );

const resolveLoadingPortLabel = detailRow => {
  if (!detailRow) return '-';
  if (detailRow.fobOriginLocationName) {
    return detailRow.fobOriginLocationName;
  }
  if (detailRow.incotermLocationId) {
    return detailRow.loadingPort || '-';
  }
  const names = getRowOriginNames(detailRow);
  if (isFobOriginDisplay(detailRow.route, names)) {
    const capableNames = getCapableOriginNamesFromFobLocations(
      detailRow.incotermFOBLocations
    );
    const fallbackNames = (
      Array.isArray(detailRow.selectedCapableLocations)
        ? detailRow.selectedCapableLocations
        : []
    )
      .map(loc => loc?.name)
      .filter(Boolean);
    return renderSelectedOriginLocations(
      capableNames.length ? capableNames : fallbackNames
    );
  }
  if (isFobIncotermRow(detailRow) || names.length) {
    return formatFobLocationsJoined(names) || detailRow.loadingPort || '-';
  }
  return '-';
};

const PortSummary = ({
  label,
  port,
  parcelSize,
  dateRange,
  locationNames = null,
  route = null,
}) => {
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  return (
    <Col md={2}>
      <div className='text-muted fw-semibold fs-12 mb-2'>{label}</div>
      <div className='fs-13'>
        <span className='text-muted d-block fs-12'>Port :</span>
        <span className='text-dark d-block mb-2'>
          {hasLocationNames ? (
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          ) : (
            displayValue(port)
          )}
        </span>
        <span className='text-muted d-block fs-12'>Parcel Size :</span>
        <span className='text-dark d-block mb-2'>
          {displayValue(parcelSize)}
        </span>
        {dateRange && (
          <>
            <span className='text-muted d-block fs-12'>Date :</span>
            <span className='text-dark d-block'>{dateRange}</span>
          </>
        )}
      </div>
    </Col>
  );
};

const renderHighlightedText = (value, highlighted) => (
  <span
    className={highlighted ? 'fw-semibold' : 'text-dark'}
    style={
      highlighted
        ? {
            ...counterValueStyle,
            padding: '0 4px',
            borderRadius: 2,
            display: 'inline-block',
          }
        : undefined
    }
  >
    {value}
  </span>
);

const renderPortBlock = (
  port,
  parcelSize,
  {
    startDate,
    endDate,
    fallbackDate,
    highlight = {},
    locationNames = null,
    route = null,
  } = {}
) => {
  const dateText = formatPortDateRange(startDate, endDate, fallbackDate);
  const highlightPort = highlight === true || highlight?.port;
  const highlightParcel = highlight === true || highlight?.parcelSize;
  const highlightDate = highlight === true || highlight?.date;
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  return (
    <div className='fs-12 lh-base d-flex flex-column gap-1'>
      <span>
        <span className='text-muted'>Port : </span>
        {hasLocationNames ? (
          <span
            className={highlightPort ? 'fw-semibold' : 'text-dark'}
            style={highlightPort ? counterValueStyle : undefined}
          >
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          </span>
        ) : (
          renderHighlightedText(formatPortLocation(port), highlightPort)
        )}
      </span>
      <span>
        <span className='text-muted'>Parcel Size : </span>
        {renderHighlightedText(
          formatParcelSize(parcelSize, 'MB'),
          highlightParcel
        )}
      </span>
      <span>
        <span className='text-muted'>ALD/ADD : </span>
        {renderHighlightedText(dateText, highlightDate)}
      </span>
    </div>
  );
};

const splitAlphaPrice = (value, suffix = 'USD/BBL') => {
  if (!hasFilledValue(value)) return null;
  const text = String(value).trim();
  const match = text.match(/^(.*?)\s+(USD\/(?:BBL|MT))$/i);
  const amount = (match?.[1] || text).trim();
  const unit = match?.[2] || suffix;
  if (!amount) return null;
  return { amount, unit };
};

const AlphaPriceText = ({ value, suffix = 'USD/BBL' }) => {
  const parsed = splitAlphaPrice(value, suffix);
  if (!parsed) return '-';
  return (
    <span className='d-inline-flex flex-column lh-sm'>
      <span>{parsed.amount}</span>
      <span className='text-muted fw-normal fs-11'>{parsed.unit}</span>
    </span>
  );
};

const formatTimelineRange = (timeline, portNumber = 1, fallback = null) => {
  if (timeline) {
    const startKey = portNumber === 1 ? 'startDate' : `startDate${portNumber}`;
    const endKey = portNumber === 1 ? 'endDate' : `endDate${portNumber}`;
    const start = timeline[startKey];
    const end = timeline[endKey];
    if (start || end) {
      if (start && end) return `${start} - ${end}`;
      return start || end;
    }
  }
  const fallbackText = String(fallback ?? '').trim();
  if (fallbackText && fallbackText !== '-') return fallbackText;
  return null;
};

const getProductLabel = procurement => {
  const productName =
    procurement?.productType?.product?.name ??
    procurement?.productType?.Product?.Name;
  const productDesc =
    procurement?.productType?.description ??
    procurement?.productType?.Description;

  if (productName && productDesc) return `${productName} - ${productDesc}`;
  return productName || productDesc || '-';
};

const getQuantityLabel = parcel => {
  const quantities =
    parcel?.quantities ?? parcel?.listQuantityInformation ?? [];
  if (!quantities.length) return '-';
  return quantities
    .map(item => `${displayParcelValue(item.quantity)} MB`)
    .join(', ');
};

const getUsesAlphaField = procurement =>
  !usesDyedUndyedAlpha(getProcurementProductNameId(procurement));

const findDetailRowsForBidder = (
  bidder,
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails
) => {
  const bidderRows = (deliveryDetails ?? []).filter(row =>
    deliveryDetailRowMatchesBidder(row, bidder)
  );

  const requirementLocationId = requirement?.incotermLocationId
    ? String(requirement.incotermLocationId)
    : '';

  const matchLocation = row => {
    if (!requirementLocationId) return true;
    return String(row?.incotermLocationId ?? '') === requirementLocationId;
  };

  const byIndex = bidderRows.filter(
    row => row.requirementIndex === requirementIndex && matchLocation(row)
  );
  if (byIndex.length) return byIndex;

  const byRequirement = bidderRows.filter(
    row =>
      offerRowMatchesRequirement(row, requirement, parcelId) &&
      matchLocation(row)
  );
  if (byRequirement.length) return byRequirement;

  if (
    bidderRows[requirementIndex] &&
    matchLocation(bidderRows[requirementIndex])
  ) {
    return [bidderRows[requirementIndex]];
  }

  const parcelRows = (deliveryDetails ?? []).filter(
    row => !parcelId || String(row.quantityDeliveryTermId) === String(parcelId)
  );

  const uniqueParentIds = new Set(
    parcelRows
      .map(row => row.parentId ?? row.procurementMitraId)
      .filter(Boolean)
      .map(String)
  );
  const canUseIndexFallback = uniqueParentIds.size <= 1;

  if (canUseIndexFallback) {
    const fallback = parcelRows.filter(
      row =>
        (row.requirementIndex === requirementIndex ||
          parcelRows[requirementIndex] === row) &&
        matchLocation(row)
    );
    if (fallback.length) return fallback;
    const single = parcelRows[requirementIndex];
    if (single && matchLocation(single)) return [single];
  }

  return [];
};

const hasFilledValue = value =>
  value != null && String(value).trim() !== '' && String(value).trim() !== '-';

const pickFirstFilled = (...values) => {
  for (const value of values) {
    if (hasFilledValue(value)) return value;
  }
  return null;
};

const requirementHasContent = requirement =>
  [
    requirement?.deliveryBasis,
    requirement?.incoterm,
    requirement?.route,
    requirement?.port1,
    requirement?.port2,
    requirement?.port3,
  ].some(hasFilledValue);

const requirementHasOfferData = ({
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
  bidders,
  procurement,
  improvementOnly = false,
}) => {
  const rows = getCompliantBidderRows({
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
    procurement,
  });
  return (improvementOnly ? filterImprovementOfferRows(rows) : rows).length > 0;
};

const hasCounterPricing = (row, usesAlphaField) =>
  !!(
    row?.quantityInformationDetailCounterId ||
    (usesAlphaField
      ? isAlphaFilled(row?.counterAlpha) || isAlphaFilled(row?.alpha2)
      : isAlphaFilled(row?.counterDyed) ||
        isAlphaFilled(row?.counterUndyed) ||
        isAlphaFilled(row?.dyed2) ||
        isAlphaFilled(row?.undyed2))
  );

const getOfferRowKey = (
  parcelId,
  requirementIndex,
  bidder,
  detailRow,
  kind = 'offer'
) =>
  `${parcelId ?? 'parcel'}-${requirementIndex}-${bidder?.id ?? bidder?.idOffer}-${detailRow?.quantityInformationDetailId ?? detailRow?.id ?? 'x'}-${detailRow?.incotermLocationId ?? 'noloc'}-${kind}`;

const getCompliantBidderRows = ({
  bidders,
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
  procurement,
}) => {
  if (!bidders?.length) return [];

  const usesAlphaField = getUsesAlphaField(procurement);

  return bidders.flatMap(bidder => {
    const detailRows = findDetailRowsForBidder(
      bidder,
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails
    );
    if (!detailRows.length) return [];

    return detailRows.flatMap(detailRow => {
      const rows = [];
      if (isRowCapable(detailRow, usesAlphaField)) {
        rows.push({
          bidder,
          detailRow,
          kind: 'offer',
          rowKey: getOfferRowKey(
            parcelId,
            requirementIndex,
            bidder,
            detailRow,
            'offer'
          ),
        });
      }
      if (hasCounterPricing(detailRow, usesAlphaField)) {
        rows.push({
          bidder,
          detailRow,
          kind: 'counter',
          rowKey: getOfferRowKey(
            parcelId,
            requirementIndex,
            bidder,
            detailRow,
            'counter'
          ),
        });
      }
      return rows;
    });
  });
};

const getFobOriginSelectionGroupKey = row => {
  const detailRow = row?.detailRow ?? {};
  if (!detailRow?.incotermLocationId) return null;
  return [
    row?.bidder?.id ?? row?.bidder?.idOffer ?? '',
    row?.kind ?? 'offer',
    detailRow.quantityInformationDetailId ?? detailRow.id ?? '',
    detailRow.quantityIncotermId ?? '',
  ].join('|');
};

const OfferDetailRows = ({
  compliantRows,
  isLoading,
  selectedRowKeys,
  onSelectRow,
  procurement,
  onViewDetail,
  improvementOnly = false,
  showCheckboxes = false,
  selectionLocked = false,
  negotiationMode = false,
  usesDyedUndyed = true,
  alphaColumns = { dyed: true, undyed: true },
  pricingUnit = null,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  hpsAvailable = false,
  hpsCatalog = [],
  showImprovementHpsColumns = false,
  isImprovementClosed = false,
}) => {
  // Gasoline parcels may ask for only Dyed or only Undyed. The first alpha
  // column is Dyed (gasoline) or the single alpha (other products).
  const showFirstAlpha = !usesDyedUndyed || alphaColumns.dyed;
  const showUndyedAlpha = usesDyedUndyed && alphaColumns.undyed;
  const alphaCols = Number(showFirstAlpha) + Number(showUndyedAlpha);
  const colSpan =
    (showCheckboxes ? 14 : 11) +
    alphaCols -
    (!improvementOnly && !negotiationMode ? 2 : 0) +
    (showImprovementHpsColumns ? 2 : 0);
  const statusWording = (procurement?.evaluationStatusWording || '')
    .toString()
    .toLowerCase()
    .trim();
  const statusKey = String(procurement?.evaluationStatus ?? '')
    .toLowerCase()
    .trim();
  const showCounterpartyName =
    (improvementOnly || negotiationMode) &&
    (statusWording === 'closing evaluation' ||
      statusKey === 'closingevaluation' ||
      isImprovementClosed);

  if (isLoading) {
    return <SkeletonTable inline rows={3} columns={colSpan} />;
  }

  if (!compliantRows.length) {
    return (
      <tr>
        <td colSpan={colSpan} className='text-center text-muted py-4 fs-13'>
          {improvementOnly
            ? 'Belum ada data improvement.'
            : negotiationMode
              ? 'Tidak ada Offer dengan ID Offer.'
              : 'Belum ada data offer.'}
        </td>
      </tr>
    );
  }

  const usesAlphaField = getUsesAlphaField(procurement);
  const alphaSuffix = pricingUnit || 'USD/BBL';
  const fobOriginGroupMeta = {};
  compliantRows.forEach((row, index) => {
    const groupKey = getFobOriginSelectionGroupKey(row);
    if (!groupKey) return;
    if (!fobOriginGroupMeta[groupKey]) {
      fobOriginGroupMeta[groupKey] = {
        leadIndex: index,
        count: 0,
        keys: [],
      };
    }
    fobOriginGroupMeta[groupKey].count += 1;
    fobOriginGroupMeta[groupKey].keys.push(row.rowKey);
  });

  return compliantRows.map(({ bidder, detailRow, kind, rowKey }, index) => {
    const isCounter = kind === 'counter';
    const alphaDyedValue = usesAlphaField
      ? isCounter
        ? coalesceAlphaPrice(
            detailRow.counterAlpha,
            detailRow.alpha2,
            detailRow.alphaMitra2,
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1
          )
        : coalesceAlphaPrice(
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1,
            detailRow.alpha1
          )
      : isCounter
        ? coalesceAlphaPrice(
            detailRow.counterDyed,
            detailRow.dyed2,
            detailRow.dyed,
            detailRow.unitBbl
          )
        : coalesceAlphaPrice(detailRow.dyed, detailRow.unitBbl);
    const alphaUndyedValue = usesAlphaField
      ? null
      : isCounter
        ? coalesceAlphaPrice(
            detailRow.counterUndyed,
            detailRow.undyed2,
            detailRow.undyed,
            detailRow.cbm
          )
        : coalesceAlphaPrice(detailRow.undyed, detailRow.cbm);

    const counterDeliveryBasis =
      detailRow.counterDeliveryBasis ?? detailRow.counterParcelSize ?? null;
    const deliveryBasisChanged = isCounter
      ? isCounterQuantityChanged(
          detailRow.deliveryBasis ?? detailRow.parcelSize,
          counterDeliveryBasis
        )
      : false;

    const incoterm = isCounter
      ? (detailRow.counterIncoterm ?? detailRow.incoterm)
      : detailRow.incoterm;
    const route = isCounter
      ? (detailRow.counterRoute ?? detailRow.route)
      : detailRow.route;
    const originalIncotermRoute = formatIncotermRoute(
      detailRow.incoterm,
      detailRow.route
    );
    const counterIncotermRoute = formatIncotermRoute(incoterm, route);
    const incotermRouteChanged =
      isCounter &&
      !!detailRow.counterIncoterm &&
      isCounterValueChanged(originalIncotermRoute, counterIncotermRoute);

    const originalPort1 = detailRow.port1 || detailRow.loadingPort;
    const counterPort1 =
      detailRow.counterLoadingPort || detailRow.port1 || detailRow.loadingPort;

    const alphaDyedChanged =
      isCounter &&
      !usesAlphaField &&
      isCounterValueChanged(
        detailRow.dyed ?? detailRow.unitBbl,
        detailRow.counterDyed ?? detailRow.dyed2
      );
    const alphaUndyedChanged =
      isCounter &&
      !usesAlphaField &&
      isCounterValueChanged(
        detailRow.undyed ?? detailRow.cbm,
        detailRow.counterUndyed ?? detailRow.undyed2
      );
    const alphaChanged =
      isCounter &&
      usesAlphaField &&
      isCounterValueChanged(detailRow.alpha, detailRow.counterAlpha);
    const isImprovement = isOfferRowImprovement({ bidder, detailRow, kind });
    const fobGroupKey = getFobOriginSelectionGroupKey({
      bidder,
      detailRow,
      kind,
    });
    const fobGroup = fobGroupKey ? fobOriginGroupMeta[fobGroupKey] : null;
    const isFobGroupLead = !fobGroup || fobGroup.leadIndex === index;
    const fobGroupChecked = fobGroup
      ? fobGroup.keys.every(key => selectedRowKeys.has(key))
      : selectedRowKeys.has(rowKey);
    const negotiationBlocked =
      negotiationMode &&
      !isNegotiationRowSelectable({ bidder, detailRow, kind });
    const counterUnsaved =
      negotiationBlocked && hasNegotiationOfferId(bidder?.idOffer);

    return (
      <tr
        key={rowKey ?? `${bidder.id ?? index}-${index}`}
        className='border-bottom'
      >
        {showCheckboxes && isFobGroupLead && (
          <td
            className='text-center align-middle'
            rowSpan={fobGroup?.count > 1 ? fobGroup.count : undefined}
            title={counterUnsaved ? 'Counter belum tersimpan' : undefined}
          >
            <Input
              type='checkbox'
              className='form-check-input'
              checked={fobGroupChecked}
              disabled={
                selectionLocked ||
                (!negotiationMode && isImprovement) ||
                negotiationBlocked
              }
              onChange={e => {
                if (selectionLocked) return;
                if (!negotiationMode && isImprovement) return;
                if (negotiationBlocked) return;
                onSelectRow?.(rowKey, e.target.checked);
              }}
              aria-label={`Pilih ${displayValue(bidder.idOffer)}`}
            />
          </td>
        )}
        {showCheckboxes && !negotiationMode && (
          <td className='text-dark fs-12'>
            {getOfferRowImprovementStatusLabel({ bidder, detailRow, kind }) ===
            'Requested' ? (
              <Badge
                color='success'
                className='px-2 py-1 fs-11 fw-medium'
                style={{ whiteSpace: 'normal', lineHeight: 1.25 }}
              >
                Requested
              </Badge>
            ) : (
              <Badge
                color='secondary'
                className='px-2 py-1 fs-11 fw-medium'
                style={{ whiteSpace: 'normal', lineHeight: 1.25 }}
              >
                Not Req.
              </Badge>
            )}
          </td>
        )}
        <td
          className={
            showCounterpartyName || negotiationMode
              ? 'text-dark'
              : 'text-dark text-nowrap'
          }
          style={
            showCounterpartyName || negotiationMode
              ? {
                  maxWidth: 120,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.25,
                }
              : undefined
          }
          title={
            showCounterpartyName || negotiationMode
              ? displayValue(bidder?.companyName || bidder?.idOffer)
              : undefined
          }
        >
          {showCounterpartyName || negotiationMode ? (
            <>
              <div className='fw-semibold text-dark'>
                {displayValue(bidder?.companyName || bidder?.idOffer)}
              </div>
              {bidder?.companyName ? (
                <div className='text-muted fs-12'>
                  {displayValue(bidder.idOffer)}
                </div>
              ) : null}
            </>
          ) : (
            displayValue(bidder.idOffer)
          )}
        </td>
        <td>
          <OfferStatusBadges
            isCounter={isCounter}
            isLateSubmission={bidder?.isLateSubmission === true}
          />
        </td>
        <td
          className='text-dark'
          style={deliveryBasisChanged ? counterValueStyle : undefined}
        >
          {isCounter
            ? displayValue(
                counterDeliveryBasis != null && counterDeliveryBasis !== ''
                  ? formatParcelSize(counterDeliveryBasis, 'MB')
                  : null
              )
            : displayValue(
                detailRow.deliveryBasis != null &&
                  detailRow.deliveryBasis !== ''
                  ? formatParcelSize(detailRow.deliveryBasis, 'MB')
                  : detailRow.parcelSize != null && detailRow.parcelSize !== ''
                    ? formatParcelSize(detailRow.parcelSize, 'MB')
                    : null
              )}
        </td>
        <td
          className='text-dark'
          style={incotermRouteChanged ? counterValueStyle : undefined}
        >
          {isCounter
            ? counterIncotermRoute || '-'
            : originalIncotermRoute || '-'}
        </td>
        <td className='text-dark'>
          <OperationalToleranceSectionValue
            procurement={withBidderCounter(procurement, bidder)}
            preferCounter
          />
        </td>
        <td className='text-dark fs-13 fw-medium'>
          {isCounter &&
          isFobIncotermRow({
            incoterm: detailRow.counterIncoterm ?? detailRow.incoterm,
          })
            ? detailRow.counterLoadingPort ||
              resolveLoadingPortLabel(detailRow) ||
              '-'
            : resolveLoadingPortLabel(detailRow)}
        </td>
        <td>
          {isCounter
            ? renderPortBlock(
                counterPort1,
                detailRow.counterParcelSize ?? counterDeliveryBasis,
                {
                  startDate:
                    detailRow.counterStartDate ?? detailRow.port1StartDate,
                  endDate: detailRow.counterEndDate ?? detailRow.port1EndDate,
                  fallbackDate: detailRow.counterAddAld ?? detailRow.addAld,
                  locationNames: detailRow.fobLocationNames,
                  route: detailRow.counterRoute ?? detailRow.route,
                  highlight: {
                    port: isCounterValueChanged(
                      originalPort1,
                      detailRow.counterLoadingPort
                    ),
                    parcelSize: isCounterQuantityChanged(
                      detailRow.parcelSize ?? detailRow.deliveryBasis,
                      detailRow.counterParcelSize ?? counterDeliveryBasis
                    ),
                    date: isCounterDateChanged(
                      detailRow.port1StartDate,
                      detailRow.port1EndDate,
                      detailRow.counterStartDate,
                      detailRow.counterEndDate,
                      detailRow.addAld,
                      detailRow.counterAddAld
                    ),
                  },
                }
              )
            : renderPortBlock(
                originalPort1,
                detailRow.parcelSize ?? detailRow.deliveryBasis,
                {
                  startDate: detailRow.port1StartDate,
                  endDate: detailRow.port1EndDate,
                  fallbackDate: detailRow.addAld,
                  locationNames: detailRow.fobLocationNames,
                  route: detailRow.route,
                }
              )}
        </td>
        <td>
          {isCounter
            ? detailRow.counterPort2 || detailRow.port2
              ? renderPortBlock(
                  detailRow.counterPort2 || detailRow.port2,
                  detailRow.counterParcelSize2 ?? detailRow.parcelSize2,
                  {
                    startDate:
                      detailRow.counterStartDate2 ?? detailRow.port2StartDate,
                    endDate:
                      detailRow.counterEndDate2 ?? detailRow.port2EndDate,
                    fallbackDate: detailRow.counterAddAld2 ?? detailRow.addAld2,
                    highlight: {
                      port: isCounterValueChanged(
                        detailRow.port2,
                        detailRow.counterPort2
                      ),
                      parcelSize: isCounterQuantityChanged(
                        detailRow.parcelSize2,
                        detailRow.counterParcelSize2
                      ),
                      date: isCounterDateChanged(
                        detailRow.port2StartDate,
                        detailRow.port2EndDate,
                        detailRow.counterStartDate2,
                        detailRow.counterEndDate2,
                        detailRow.addAld2,
                        detailRow.counterAddAld2
                      ),
                    },
                  }
                )
              : ''
            : detailRow.port2
              ? renderPortBlock(detailRow.port2, detailRow.parcelSize2, {
                  startDate: detailRow.port2StartDate,
                  endDate: detailRow.port2EndDate,
                  fallbackDate: detailRow.addAld2,
                })
              : ''}
        </td>
        <td>
          {isCounter
            ? detailRow.counterPort3 || detailRow.port3
              ? renderPortBlock(
                  detailRow.counterPort3 || detailRow.port3,
                  detailRow.counterParcelSize3 ?? detailRow.parcelSize3,
                  {
                    startDate:
                      detailRow.counterStartDate3 ?? detailRow.port3StartDate,
                    endDate:
                      detailRow.counterEndDate3 ?? detailRow.port3EndDate,
                    fallbackDate: detailRow.counterAddAld3 ?? detailRow.addAld3,
                    highlight: {
                      port: isCounterValueChanged(
                        detailRow.port3,
                        detailRow.counterPort3
                      ),
                      parcelSize: isCounterQuantityChanged(
                        detailRow.parcelSize3,
                        detailRow.counterParcelSize3
                      ),
                      date: isCounterDateChanged(
                        detailRow.port3StartDate,
                        detailRow.port3EndDate,
                        detailRow.counterStartDate3,
                        detailRow.counterEndDate3,
                        detailRow.addAld3,
                        detailRow.counterAddAld3
                      ),
                    },
                  }
                )
              : ''
            : detailRow.port3
              ? renderPortBlock(detailRow.port3, detailRow.parcelSize3, {
                  startDate: detailRow.port3StartDate,
                  endDate: detailRow.port3EndDate,
                  fallbackDate: detailRow.addAld3,
                })
              : ''}
        </td>
        {showFirstAlpha && (
          <td
            className='text-dark'
            style={
              alphaChanged || alphaDyedChanged ? counterValueStyle : undefined
            }
          >
            <AlphaPriceText value={alphaDyedValue} suffix={alphaSuffix} />
          </td>
        )}
        {showUndyedAlpha && (
          <td
            className='text-dark'
            style={alphaUndyedChanged ? counterValueStyle : undefined}
          >
            <AlphaPriceText value={alphaUndyedValue} suffix={alphaSuffix} />
          </td>
        )}
        {negotiationMode && (
          <>
            <td className='text-dark'>
              <HpsAlphaCell
                usesDyedUndyed={usesDyedUndyed}
                alphaColumns={alphaColumns}
                hps={getHpsAlphaValues(detailRow, {
                  isCounter,
                  hpsCatalog,
                })}
                suffix={alphaSuffix}
                hpsAvailable={hpsAvailable}
              />
            </td>
            <td>
              <MatchingHpsBadge
                matchKey={resolveMatchingHpsKey({
                  usesDyedUndyed,
                  alphaColumns,
                  offerAlpha: usesDyedUndyed ? null : alphaDyedValue,
                  offerDyed: usesDyedUndyed ? alphaDyedValue : null,
                  offerUndyed: usesDyedUndyed ? alphaUndyedValue : null,
                  hps: getHpsAlphaValues(detailRow, {
                    isCounter,
                    hpsCatalog,
                  }),
                  statusFromApi: pickFirstFilled(
                    detailRow.matchingHps,
                    detailRow.hpsMatching,
                    detailRow.matchingHpsStatus
                  ),
                  hpsAvailable,
                })}
              />
            </td>
          </>
        )}
        {improvementOnly || negotiationMode ? (
          alphaImprovementEditable ? (
            <AlphaImprovementEditableCells
              detailRow={detailRow}
              kind={kind}
              usesDyedUndyed={usesDyedUndyed}
              alphaColumns={alphaColumns}
              pricingUnit={alphaSuffix}
              onAlphaImprovementChange={onAlphaImprovementChange}
            />
          ) : usesDyedUndyed ? (
            <>
              {alphaColumns.dyed && (
                <td className='text-dark'>
                  <AlphaPriceText
                    value={getAlphaImprovementFieldValue(
                      detailRow,
                      kind,
                      'dyed'
                    )}
                    suffix={alphaSuffix}
                  />
                </td>
              )}
              {alphaColumns.undyed && (
                <td className='text-dark'>
                  <AlphaPriceText
                    value={getAlphaImprovementFieldValue(
                      detailRow,
                      kind,
                      'undyed'
                    )}
                    suffix={alphaSuffix}
                  />
                </td>
              )}
            </>
          ) : (
            <td className='text-dark'>
              <AlphaPriceText
                value={getAlphaImprovementFieldValue(detailRow, kind, 'alpha')}
                suffix={alphaSuffix}
              />
            </td>
          )
        ) : (
          <td className='text-center'>
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-eye-line'
              label='Detail'
              onClick={() => onViewDetail?.(bidder, detailRow)}
            />
          </td>
        )}
        {showImprovementHpsColumns && (
          <>
            <td className='text-dark'>
              <HpsAlphaCell
                usesDyedUndyed={usesDyedUndyed}
                alphaColumns={alphaColumns}
                hps={getHpsAlphaValues(detailRow, {
                  isCounter,
                  hpsCatalog,
                })}
                suffix={alphaSuffix}
                hpsAvailable={hpsAvailable}
              />
            </td>
            <td>
              <MatchingHpsBadge
                matchKey={resolveMatchingHpsKey({
                  usesDyedUndyed,
                  alphaColumns,
                  offerAlpha: usesDyedUndyed
                    ? null
                    : getAlphaImprovementFieldValue(detailRow, kind, 'alpha'),
                  offerDyed: usesDyedUndyed
                    ? getAlphaImprovementFieldValue(detailRow, kind, 'dyed')
                    : null,
                  offerUndyed: usesDyedUndyed
                    ? getAlphaImprovementFieldValue(detailRow, kind, 'undyed')
                    : null,
                  hps: getHpsAlphaValues(detailRow, {
                    isCounter,
                    hpsCatalog,
                  }),
                  statusFromApi: pickFirstFilled(
                    detailRow.matchingHps,
                    detailRow.hpsMatching,
                    detailRow.matchingHpsStatus
                  ),
                  hpsAvailable,
                })}
              />
            </td>
          </>
        )}
      </tr>
    );
  });
};

const RequirementCard = ({
  requirement,
  requirementIndex,
  parcelId,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  procurement,
  onViewDetail,
  improvementOnly = false,
  negotiationMode = false,
  showCheckboxes = false,
  selectionLocked = false,
  usesDyedUndyed = true,
  alphaColumns = { dyed: true, undyed: true },
  alphaColumnLabel = 'Alpha',
  pricingUnit = null,
  selectionKey,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  selectAllToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onNegotiationOfferAvailability,
  preselectedOffers = null,
  showImprovementHpsColumns = false,
  isImprovementClosed = false,
}) => {
  const filterImprovement = improvementOnly || negotiationMode;
  const hasData = useMemo(
    () =>
      (!filterImprovement && requirementHasContent(requirement)) ||
      requirementHasOfferData({
        requirement,
        requirementIndex,
        parcelId,
        deliveryDetails,
        bidders,
        procurement,
        improvementOnly: filterImprovement,
      }),
    [
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
      bidders,
      procurement,
      filterImprovement,
    ]
  );

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (hasData) setIsExpanded(true);
  }, [hasData]);

  const timeline = requirement?.timeline;
  const incotermRouteLabel = getRequirementIncotermRouteLabel(requirement);
  const selectAllRef = useRef(null);

  const requirementFobRow = useMemo(() => {
    const id = requirement?.quantityIncotermId;
    if (id === null || id === undefined || id === '') return null;
    return (
      deliveryDetails.find(
        row => String(row.quantityIncotermId ?? '') === String(id)
      ) ?? null
    );
  }, [deliveryDetails, requirement]);
  const summaryFobLocationNames = useMemo(() => {
    const id = requirement?.quantityIncotermId;
    if (id === null || id === undefined || id === '') {
      return requirementFobRow?.fobLocationNames ?? null;
    }
    const names = [];
    const seen = new Set();
    deliveryDetails.forEach(row => {
      if (String(row.quantityIncotermId ?? '') !== String(id)) return;
      const label =
        row.fobOriginLocationName ||
        (Array.isArray(row.selectedCapableLocations) &&
        row.selectedCapableLocations.length === 1
          ? row.selectedCapableLocations[0]?.name
          : null);
      if (label && !seen.has(label)) {
        seen.add(label);
        names.push(label);
      }
    });
    if (names.length) return names;
    return requirementFobRow?.fobLocationNames ?? null;
  }, [deliveryDetails, requirement, requirementFobRow]);
  const summaryFobRoute =
    requirementFobRow?.route ?? requirement?.route ?? null;

  const compliantRows = useMemo(() => {
    const rows = getCompliantBidderRows({
      bidders,
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
      procurement,
    });
    return filterImprovement ? filterImprovementOfferRows(rows) : rows;
  }, [
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
    procurement,
    filterImprovement,
  ]);

  const hasSelectableOffer = useMemo(
    () => compliantRows.some(isNegotiationRowSelectable),
    [compliantRows]
  );

  useEffect(() => {
    if (!negotiationMode || !onNegotiationOfferAvailability) return;
    if (isLoadingBidders || isLoadingDeliveryDetails) return;
    onNegotiationOfferAvailability({
      parcelId,
      requirementIndex,
      hasSelectableOffer,
    });
  }, [
    negotiationMode,
    onNegotiationOfferAvailability,
    isLoadingBidders,
    isLoadingDeliveryDetails,
    parcelId,
    requirementIndex,
    hasSelectableOffer,
  ]);

  const hpsCatalog = useMemo(
    () => buildHpsCatalog({ deliveryDetails, requirement }),
    [deliveryDetails, requirement]
  );

  const [statusFilters, setStatusFilters] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());

  const filteredRows = useMemo(
    () => filterRowsByOfferStatus(compliantRows, statusFilters),
    [compliantRows, statusFilters]
  );

  const selectableRows = useMemo(() => {
    const rows =
      negotiationMode || improvementOnly
        ? filteredRows
        : filteredRows.filter(row => !isOfferRowImprovement(row));
    if (!negotiationMode) return rows;
    return rows.filter(isNegotiationRowSelectable);
  }, [filteredRows, improvementOnly, negotiationMode]);

  useEffect(() => {
    if (!selectionClearToken) return;
    if (improvementOnly || negotiationMode) {
      setSelectedRowKeys(new Set());
      return;
    }
    // Keep Requested rows checked; only clear selectable offers.
    setSelectedRowKeys(
      new Set(
        compliantRows
          .filter(row => isOfferRowImprovement(row))
          .map(row => row.rowKey)
          .filter(Boolean)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionClearToken]);

  useEffect(() => {
    if (selectionLocked) {
      setSelectedRowKeys(new Set());
    }
  }, [selectionLocked]);

  useEffect(() => {
    if (!selectAllToken || negotiationMode || selectionLocked) return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      compliantRows
        .filter(row => isOfferRowImprovement(row))
        .forEach(row => {
          if (row.rowKey) next.add(row.rowKey);
        });
      selectableRows.forEach(row => {
        if (row.rowKey) next.add(row.rowKey);
      });
      return next;
    });
    // Only react to explicit Select all action, not filter/row churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAllToken]);

  useEffect(() => {
    const validKeys = new Set(compliantRows.map(row => row.rowKey));
    setSelectedRowKeys(prev => {
      const next = new Set([...prev].filter(key => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [compliantRows]);

  // Prefill (saved package): check matching rows once, after rows load.
  const preselectAppliedRef = useRef(false);
  useEffect(() => {
    if (preselectAppliedRef.current || !negotiationMode) return;
    if (!preselectedOffers?.length || !compliantRows.length) return;
    preselectAppliedRef.current = true;
    const keys = getPreselectedRowKeys(
      compliantRows,
      preselectedOffers,
      'rowKey'
    );
    if (keys.length) setSelectedRowKeys(new Set(keys));
  }, [negotiationMode, preselectedOffers, compliantRows]);

  useEffect(() => {
    if (improvementOnly || negotiationMode) return;
    const improvementKeys = compliantRows
      .filter(row => isOfferRowImprovement(row))
      .map(row => row.rowKey)
      .filter(Boolean);
    if (!improvementKeys.length) return;

    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      let changed = false;
      improvementKeys.forEach(key => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [compliantRows, improvementOnly, negotiationMode]);

  useEffect(() => {
    if (
      !showCheckboxes ||
      !selectionKey ||
      !onRequirementSelectionChange ||
      selectionLocked
    ) {
      return;
    }
    const collect = negotiationMode
      ? collectNegotiationSelections
      : collectImprovementSelections;
    onRequirementSelectionChange(
      selectionKey,
      collect({
        compliantRows,
        selectedKeys: [...selectedRowKeys],
        parcelId,
        requirementIndex,
        requirement,
        keyField: 'rowKey',
      })
    );
  }, [
    selectionKey,
    selectedRowKeys,
    compliantRows,
    parcelId,
    requirementIndex,
    requirement,
    onRequirementSelectionChange,
    showCheckboxes,
    negotiationMode,
    selectionLocked,
  ]);

  useEffect(() => {
    if (!selectionLocked || !selectionKey || !onRequirementSelectionChange)
      return;
    onRequirementSelectionChange(selectionKey, []);
  }, [selectionLocked, selectionKey, onRequirementSelectionChange]);

  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every(row => selectedRowKeys.has(row.rowKey));
  const someSelected = selectableRows.some(row =>
    selectedRowKeys.has(row.rowKey)
  );

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const handleSelectAll = checked => {
    if (selectionLocked) return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      selectableRows.forEach(({ rowKey }) => {
        if (checked) next.add(rowKey);
        else next.delete(rowKey);
      });
      return next;
    });
  };

  const handleSelectRow = (rowKey, checked) => {
    if (selectionLocked) return;
    const row = compliantRows.find(entry => entry.rowKey === rowKey);
    if (
      !negotiationMode &&
      !improvementOnly &&
      row &&
      isOfferRowImprovement(row)
    )
      return;
    if (negotiationMode && !isNegotiationRowSelectable(row)) return;
    const groupKey = getFobOriginSelectionGroupKey(row);
    const keysToToggle = groupKey
      ? compliantRows
          .filter(
            entry =>
              getFobOriginSelectionGroupKey(entry) === groupKey &&
              (negotiationMode ||
                improvementOnly ||
                !isOfferRowImprovement(entry))
          )
          .map(entry => entry.rowKey)
          .filter(Boolean)
      : [rowKey];

    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      keysToToggle.forEach(key => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  return (
    <Card
      className={`border shadow-none mb-3${selectionLocked ? ' opacity-75' : ''}`}
    >
      <CardHeader
        className='bg-light border-bottom p-3 d-flex justify-content-between align-items-center cursor-pointer'
        onClick={() => setIsExpanded(prev => !prev)}
        role='button'
        aria-expanded={isExpanded}
        aria-controls={`requirement-body-${requirementIndex}`}
      >
        <div>
          <h6 className='mb-0 text-dark fw-bold fs-13'>
            Requirement {requirementIndex + 1}
            {selectionLocked ? (
              <Badge color='secondary' className='ms-2 fs-11 fw-medium'>
                Locked
              </Badge>
            ) : null}
          </h6>
          <span className='text-muted fs-12'>{incotermRouteLabel}</span>
          {selectionLocked ? (
            <div className='text-muted fs-11 mt-1'>
              Another requirement is selected for this Delivery Unit.
            </div>
          ) : null}
        </div>
        <i
          className={`fs-20 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}
        ></i>
      </CardHeader>

      <Collapse isOpen={isExpanded}>
        <CardBody id={`requirement-body-${requirementIndex}`} className='p-0'>
          <div className='p-3 border-bottom'>
            <Row className='g-3'>
              <Col md={2}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Delivery Basis
                </div>
                <div className='text-dark fs-13 mt-4'>
                  {displayValue(requirement.deliveryBasis)}
                </div>
              </Col>
              <Col md={2}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Incoterm - Route
                </div>
                <div className='text-dark fs-13 mt-4'>{incotermRouteLabel}</div>
              </Col>
              <Col md={2}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Operational Tolerances
                </div>
                <div className='text-dark fs-13 mt-4'>
                  <OperationalToleranceSectionValue
                    procurement={procurement}
                    showCounter={false}
                  />
                </div>
              </Col>
              <PortSummary
                label='Port 1'
                port={requirement.port1}
                parcelSize={requirement.dischargeQty1}
                dateRange={formatTimelineRange(
                  timeline,
                  1,
                  requirement.aldAdd1
                )}
                locationNames={summaryFobLocationNames}
                route={summaryFobRoute}
              />
              <PortSummary
                label='Port 2'
                port={requirement.port2}
                parcelSize={requirement.dischargeQty2}
                dateRange={formatTimelineRange(
                  timeline,
                  2,
                  requirement.aldAdd2
                )}
              />
              <PortSummary
                label='Port 3'
                port={requirement.port3}
                parcelSize={requirement.dischargeQty3}
                dateRange={formatTimelineRange(
                  timeline,
                  3,
                  requirement.aldAdd3
                )}
              />
            </Row>
          </div>

          <div className='table-responsive m-3 border rounded'>
            <Table
              className='table-borderless align-middle mb-0 w-100 offer-compare-table'
              style={OFFER_TABLE_STYLE}
              aria-label='Offer comparison'
            >
              <thead className='bg-light text-muted fs-12 border-bottom'>
                <tr>
                  {showCheckboxes && (
                    <th
                      scope='col'
                      style={thStyle('check')}
                      className='text-center'
                    >
                      <div className='d-inline-flex flex-column align-items-center gap-1'>
                        <Input
                          innerRef={selectAllRef}
                          type='checkbox'
                          className='form-check-input'
                          checked={allSelected}
                          disabled={
                            selectionLocked ||
                            isLoadingBidders ||
                            isLoadingDeliveryDetails ||
                            !selectableRows.length
                          }
                          onChange={e => handleSelectAll(e.target.checked)}
                          title='Select all offers in this requirement'
                          aria-label='Select all offers in this requirement'
                        />
                        <span className='fs-10 text-muted lh-1'>All</span>
                      </div>
                    </th>
                  )}
                  {showCheckboxes && !negotiationMode && (
                    <th scope='col' style={thStyle('statusImprovement')}>
                      <ThLabel {...OFFER_HEADER.improvement} />
                    </th>
                  )}
                  <th scope='col' style={thStyle('idOffer')}>
                    <ThLabel {...OFFER_HEADER.idOffer} />
                  </th>
                  <th scope='col' style={thStyle('status')}>
                    <div className='d-flex flex-column gap-1'>
                      <span>Status</span>
                      <OfferStatusFilter
                        value={statusFilters}
                        onChange={setStatusFilters}
                      />
                    </div>
                  </th>
                  <th scope='col' style={thStyle('deliveryBasis')}>
                    <ThLabel {...OFFER_HEADER.deliveryBasis} />
                  </th>
                  <th scope='col' style={thStyle('incoterm')}>
                    <ThLabel {...OFFER_HEADER.incoterm} />
                  </th>
                  <th scope='col' style={thStyle('ot')}>
                    <ThLabel {...OFFER_HEADER.ot} />
                  </th>
                  <th scope='col' style={thStyle('loadingPort')}>
                    <ThLabel {...OFFER_HEADER.loadingPort} />
                  </th>
                  <th scope='col' style={thStyle('port')}>
                    <ThLabel {...OFFER_HEADER.port1} />
                  </th>
                  <th scope='col' style={thStyle('port')}>
                    <ThLabel {...OFFER_HEADER.port2} />
                  </th>
                  <th scope='col' style={thStyle('port')}>
                    <ThLabel {...OFFER_HEADER.port3} />
                  </th>
                  {(!usesDyedUndyed || alphaColumns.dyed) && (
                    <th scope='col' style={thStyle('alpha')}>
                      {usesDyedUndyed ? (
                        <ThLabel {...OFFER_HEADER.alphaDyed} />
                      ) : (
                        <ThLabel
                          title={alphaColumnLabel}
                          lines={[alphaColumnLabel]}
                        />
                      )}
                    </th>
                  )}
                  {usesDyedUndyed && alphaColumns.undyed && (
                    <th scope='col' style={thStyle('alpha')}>
                      <ThLabel {...OFFER_HEADER.alphaUndyed} />
                    </th>
                  )}
                  {negotiationMode && (
                    <>
                      <th scope='col' style={thStyle('hps')}>
                        <ThLabel {...OFFER_HEADER.hpsAlpha} />
                      </th>
                      <th scope='col' style={thStyle('matching')}>
                        <ThLabel {...OFFER_HEADER.matchingHps} />
                      </th>
                    </>
                  )}
                  {improvementOnly || negotiationMode ? (
                    usesDyedUndyed ? (
                      <>
                        {alphaColumns.dyed && (
                          <th scope='col' style={thStyle('alpha')}>
                            <ThLabel {...OFFER_HEADER.alphaDyedImp} />
                          </th>
                        )}
                        {alphaColumns.undyed && (
                          <th scope='col' style={thStyle('alpha')}>
                            <ThLabel {...OFFER_HEADER.alphaUndyedImp} />
                          </th>
                        )}
                      </>
                    ) : (
                      <th scope='col' style={thStyle('alpha')}>
                        <ThLabel {...OFFER_HEADER.alphaImp} />
                      </th>
                    )
                  ) : (
                    <th scope='col' style={thStyle('action')}>
                      <ThLabel {...OFFER_HEADER.action} />
                    </th>
                  )}
                  {showImprovementHpsColumns && (
                    <>
                      <th scope='col' style={thStyle('hps')}>
                        <ThLabel {...OFFER_HEADER.hpsAlpha} />
                      </th>
                      <th scope='col' style={thStyle('matching')}>
                        <ThLabel {...OFFER_HEADER.matchingHps} />
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className='fs-13'>
                <OfferDetailRows
                  compliantRows={filteredRows}
                  isLoading={isLoadingBidders || isLoadingDeliveryDetails}
                  selectedRowKeys={selectedRowKeys}
                  onSelectRow={handleSelectRow}
                  procurement={procurement}
                  onViewDetail={onViewDetail}
                  improvementOnly={improvementOnly}
                  showCheckboxes={showCheckboxes}
                  selectionLocked={selectionLocked}
                  negotiationMode={negotiationMode}
                  usesDyedUndyed={usesDyedUndyed}
                  alphaColumns={alphaColumns}
                  pricingUnit={pricingUnit}
                  alphaImprovementEditable={alphaImprovementEditable}
                  onAlphaImprovementChange={onAlphaImprovementChange}
                  hpsAvailable={isHpsAvailable(procurement)}
                  hpsCatalog={hpsCatalog}
                  showImprovementHpsColumns={showImprovementHpsColumns}
                  isImprovementClosed={isImprovementClosed}
                />
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Collapse>
    </Card>
  );
};

const ParcelOfferCard = ({
  parcel,
  procurement,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  onViewDetail,
  improvementOnly = false,
  negotiationMode = false,
  showCheckboxes = false,
  focusRequirementIndex,
  usesDyedUndyed = true,
  alphaColumnLabel = 'Alpha',
  pricingUnit = null,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  selectAllToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onNegotiationOfferAvailability,
  preselectedOffers = null,
  showImprovementHpsColumns = false,
  isImprovementClosed = false,
}) => {
  const requirements = useMemo(
    () => buildRequirementsFromParcel(parcel),
    [parcel]
  );
  const alphaColumns = resolveDyedUndyedColumns(usesDyedUndyed, parcel);
  const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
  const [activeNegotiationKey, setActiveNegotiationKey] = useState(null);
  const parcelDeliveryDetails = useMemo(
    () =>
      parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails,
    [deliveryDetails, parcelId]
  );

  useEffect(() => {
    if (!selectionClearToken) return;
    setActiveNegotiationKey(null);
  }, [selectionClearToken]);

  const handleRequirementSelectionChange = useCallback(
    (selectionKey, entries) => {
      if (negotiationMode) {
        if (entries?.length > 0) {
          setActiveNegotiationKey(selectionKey);
        } else if (activeNegotiationKey === selectionKey) {
          setActiveNegotiationKey(null);
        }
      }
      onRequirementSelectionChange?.(selectionKey, entries);
    },
    [negotiationMode, activeNegotiationKey, onRequirementSelectionChange]
  );

  const deliveryContent = (
    <CardBody className='p-4'>
      <Row className='mb-4'>
        <Col md={4} className='border-end'>
          <div className='text-dark fw-semibold fs-13 mb-1'>Product Name</div>
          <div className='text-muted fs-13'>{getProductLabel(procurement)}</div>
        </Col>
        <Col md={4} className='border-end'>
          <div className='text-dark fw-semibold fs-13 mb-1'>Quantity</div>
          <div className='text-muted fs-13'>{getQuantityLabel(parcel)}</div>
        </Col>
        <Col md={4}>
          <div className='text-dark fw-semibold fs-13 mb-1'>
            Operation Tolerance
          </div>
          <div className='text-muted fs-13'>
            <OperationalToleranceSectionValue
              procurement={procurement}
              showCounter={false}
            />
          </div>
        </Col>
      </Row>

      {requirements.map((requirement, index) => {
        if (
          focusRequirementIndex != null &&
          Number(focusRequirementIndex) !== Number(index)
        ) {
          return null;
        }
        const selectionKey = `${parcelId || 'parcel'}-req-${index}`;
        const selectionLocked =
          negotiationMode &&
          focusRequirementIndex == null &&
          Boolean(activeNegotiationKey) &&
          activeNegotiationKey !== selectionKey;
        return (
          <RequirementCard
            key={`${parcel?.id || 'parcel'}-req-${index}`}
            selectionKey={selectionKey}
            requirement={requirement}
            requirementIndex={index}
            bidders={bidders}
            isLoadingBidders={isLoadingBidders}
            parcelId={parcelId}
            deliveryDetails={parcelDeliveryDetails}
            isLoadingDeliveryDetails={isLoadingDeliveryDetails}
            procurement={procurement}
            onViewDetail={onViewDetail}
            improvementOnly={improvementOnly}
            negotiationMode={negotiationMode}
            showCheckboxes={showCheckboxes}
            selectionLocked={selectionLocked}
            usesDyedUndyed={usesDyedUndyed}
            alphaColumns={alphaColumns}
            alphaColumnLabel={alphaColumnLabel}
            pricingUnit={pricingUnit}
            onRequirementSelectionChange={handleRequirementSelectionChange}
            selectionClearToken={selectionClearToken}
            selectAllToken={selectAllToken}
            alphaImprovementEditable={alphaImprovementEditable}
            onAlphaImprovementChange={onAlphaImprovementChange}
            onNegotiationOfferAvailability={onNegotiationOfferAvailability}
            preselectedOffers={preselectedOffers}
            showImprovementHpsColumns={showImprovementHpsColumns}
            isImprovementClosed={isImprovementClosed}
          />
        );
      })}
    </CardBody>
  );

  return (
    <Card className='border shadow-none mb-4'>
      <CardHeader className='bg-light border-bottom p-3'>
        <h6 className='mb-0 text-dark fw-bold fs-14'>
          Delivery Unit : {displayValue(parcel?.deliveryUnit, '-')}
        </h6>
      </CardHeader>
      <ParcelDeliveryPricingTabs
        parcel={parcel}
        deliveryContent={deliveryContent}
      />
    </Card>
  );
};

const OfferTableNonLpg = ({
  procurementId,
  procurement,
  parcels = [],
  isLoadingParcels = false,
  onSelectedOffersChange,
  selectionClearToken = 0,
  viewMode = 'offer',
  selectionDisabled = false,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onImprovementMeta,
  focusParcelId,
  focusRequirementIndex,
  isActive = true,
  onNegotiationOfferAvailability,
  // TE Negotiation prefill: saved partners to pre-check.
  preselectedOffers = null,
  // IC (mitra): tampilkan hanya bidder milik mitra login.
  bidderId = null,
}) => {
  const improvementOnly = viewMode === 'improvement';
  const negotiationMode = viewMode === 'negotiation';
  // HPS is evaluator-only; IC mitra view always passes bidderId.
  const showImprovementHpsColumns = improvementOnly && bidderId == null;
  const showCheckboxes =
    negotiationMode || (viewMode === 'offer' && !selectionDisabled);
  const filterImprovement = improvementOnly || negotiationMode;
  const { items: allBidders, isLoading: isLoadingBidders } = useSubmittedBidder(
    procurementId,
    {
      isActive,
      refreshToken: selectionClearToken,
      // Negotiation needs every bidder, not the first page of 20.
      pageSize: negotiationMode ? 500 : undefined,
    }
  );
  const bidders = useMemo(
    () =>
      bidderId
        ? (allBidders ?? []).filter(
            bidder => String(bidder?.id) === String(bidderId)
          )
        : allBidders,
    [allBidders, bidderId]
  );

  const baseDeliveryDetails = useMemo(
    () => flattenSubmittedBidderDeliveryDetails(bidders),
    [bidders]
  );

  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [hasEnrichmentSettled, setHasEnrichmentSettled] = useState(false);
  const improvementClosingDate = improvementOnly
    ? resolveImprovementClosingDate(deliveryDetails)
    : null;
  const isImprovementClosed =
    improvementOnly &&
    Boolean(improvementClosingDate) &&
    !isImprovementClosingActive(improvementClosingDate);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [pricingUnit, setPricingUnit] = useState(null);
  const [selectionByRequirement, setSelectionByRequirement] = useState({});
  const [selectAllToken, setSelectAllToken] = useState(0);
  const [localClearToken, setLocalClearToken] = useState(0);
  const mergedClearToken = selectionClearToken + localClearToken;
  const selectedCount = useMemo(
    () =>
      Object.values(selectionByRequirement).reduce(
        (total, entries) => total + (entries?.length || 0),
        0
      ),
    [selectionByRequirement]
  );

  const handleRequirementSelectionChange = useCallback(
    (selectionKey, entries) => {
      setSelectionByRequirement(prev => {
        const prevEntries = prev[selectionKey] || [];
        if (
          prevEntries.length === entries.length &&
          prevEntries.every(
            (entry, index) =>
              entry.quantityIncotermId === entries[index]?.quantityIncotermId &&
              entry.kind === entries[index]?.kind &&
              entry.idOffer === entries[index]?.idOffer &&
              entry.rowKey === entries[index]?.rowKey
          )
        ) {
          return prev;
        }
        return { ...prev, [selectionKey]: entries };
      });
    },
    []
  );

  useEffect(() => {
    if (!showCheckboxes || !onSelectedOffersChange) return;
    onSelectedOffersChange(
      Object.values(selectionByRequirement).flatMap(entries => entries || [])
    );
  }, [selectionByRequirement, onSelectedOffersChange, showCheckboxes]);

  useEffect(() => {
    if (!mergedClearToken) return;
    setSelectionByRequirement({});
  }, [mergedClearToken]);

  const usesDyedUndyed = usesDyedUndyedAlpha(
    getProcurementProductNameId(procurement)
  );
  const alphaColumnLabel = getAlphaColumnLabel(pricingUnit);

  useEffect(() => {
    let cancelled = false;

    const enrichDetails = async () => {
      setHasEnrichmentSettled(false);
      if ((improvementOnly || negotiationMode) && onImprovementMeta) {
        onImprovementMeta({ ready: false });
      }

      if (isLoadingBidders) {
        return;
      }

      if (!baseDeliveryDetails.length) {
        if (!cancelled) {
          setDeliveryDetails([]);
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
        return;
      }

      const token = getAuthToken();
      if (!token || !procurementId) {
        try {
          const expanded = await enrichItemsWithFobOriginParcel({
            items: baseDeliveryDetails,
          });
          if (!cancelled) setDeliveryDetails(expanded);
        } catch (error) {
          if (!cancelled) setDeliveryDetails(baseDeliveryDetails);
        } finally {
          if (!cancelled) {
            setIsEnriching(false);
            setHasEnrichmentSettled(true);
          }
        }
        return;
      }

      setIsEnriching(true);
      try {
        const byTermId = new Map();
        baseDeliveryDetails.forEach(row => {
          const termId = row.quantityDeliveryTermId ?? 'default';
          if (!byTermId.has(termId)) byTermId.set(termId, []);
          byTermId.get(termId).push(row);
        });

        const enriched = [];
        // Cache request per siklus enrich (key URL), dibuang tiap re-run.
        const requestCache = new Map();
        for (const [termId, items] of byTermId.entries()) {
          let nextItems = items;
          if (termId !== 'default') {
            try {
              const bidderMap = Object.fromEntries(
                (bidders || []).map(bidder => [String(bidder.id), bidder])
              );
              // be#456: satu get-delivery-detail per bidder (nilai offer per mitra).
              nextItems = await enrichRowsPerBidder(
                nextItems,
                (bidderItems, procurementMitraId) =>
                  mergeBidderDeliveryDetail({
                    procurementId,
                    termId,
                    token,
                    items: bidderItems,
                    procurementMitraId,
                    bidderMap,
                    cache: requestCache,
                  })
              );
            } catch (error) {
              // keep submitted-bidder rows when delivery-detail merge fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithFobLocations({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
              });
            } catch (error) {
              // keep base items when FOB enrichment fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithCounterOffers({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
                requireIncotermCounter: false,
                matchCounterOwner: negotiationMode,
              });
            } catch (error) {
              // keep base items when counter enrichment fails
            }
          }
          try {
            // be#456: FOB parcel per bidder; cache = 1 fetch per bidder lintas term.
            nextItems = await enrichRowsPerBidder(
              nextItems,
              (bidderItems, procurementMitraId) =>
                enrichItemsWithFobOriginParcel({
                  procurementId,
                  token,
                  items: bidderItems,
                  procurementMitraId,
                  cache: requestCache,
                })
            );
          } catch (error) {
            // keep base items when FOB Origin parcel expansion fails
          }
          enriched.push(...nextItems);
        }

        if (!cancelled) setDeliveryDetails(enriched);
      } finally {
        if (!cancelled) {
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
      }
    };

    enrichDetails();
    return () => {
      cancelled = true;
    };
  }, [
    baseDeliveryDetails,
    procurementId,
    selectionClearToken,
    bidders,
    isLoadingBidders,
    improvementOnly,
    negotiationMode,
    onImprovementMeta,
  ]);

  useEffect(() => {
    if (!(improvementOnly || negotiationMode) || !onImprovementMeta) return;
    if (isLoadingBidders || !hasEnrichmentSettled || isEnriching) return;
    onImprovementMeta({ deliveryDetails, ready: true });
  }, [
    deliveryDetails,
    improvementOnly,
    negotiationMode,
    onImprovementMeta,
    hasEnrichmentSettled,
    isEnriching,
    isLoadingBidders,
  ]);

  useEffect(() => {
    if (usesDyedUndyed) {
      setPricingUnit(null);
      return;
    }
    const productTypeId = getProcurementProductTypeId(procurement);
    const token = getAuthToken();
    let cancelled = false;
    fetchProductTypePricingUnit(productTypeId, token).then(unit => {
      if (!cancelled) setPricingUnit(unit);
    });
    return () => {
      cancelled = true;
    };
  }, [usesDyedUndyed, procurement]);

  const handleViewOfferDetail = (bidder, detailRow) => {
    if (!bidder?.id) return;
    setSelectedOffer({ bidder, detailRow });
    setIsDetailModalOpen(true);
  };

  const toggleDetailModal = () => {
    setIsDetailModalOpen(prev => !prev);
    if (isDetailModalOpen) {
      setSelectedOffer(null);
    }
  };

  const hasImprovementData = useMemo(() => {
    if (!filterImprovement) return true;
    const parcelItems = parcels.length
      ? parcels
      : [{ id: 'default', deliveryUnit: '-' }];
    return parcelItems.some(parcel => {
      const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
      const parcelDeliveryDetails = parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails;
      return buildRequirementsFromParcel(parcel).some((requirement, index) =>
        requirementHasOfferData({
          requirement,
          requirementIndex: index,
          parcelId,
          deliveryDetails: parcelDeliveryDetails,
          bidders,
          procurement,
          improvementOnly: true,
        })
      );
    });
  }, [filterImprovement, parcels, deliveryDetails, bidders, procurement]);

  if (isLoadingParcels || isLoadingBidders) {
    return <SkeletonTable rows={5} columns={8} />;
  }

  if (filterImprovement && (!hasEnrichmentSettled || isEnriching)) {
    return <SkeletonTable rows={5} columns={8} />;
  }

  if (!bidders.length) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        {filterImprovement
          ? 'Belum ada data improvement.'
          : 'Belum ada penawaran yang disubmit.'}
      </div>
    );
  }

  if (filterImprovement && !hasImprovementData) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        Belum ada data improvement.
      </div>
    );
  }

  const parcelItems = (
    parcels.length ? parcels : [{ id: 'default', deliveryUnit: '-' }]
  ).filter(parcel => {
    if (focusParcelId == null || focusParcelId === '') return true;
    const id = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
    return String(id) === String(focusParcelId);
  });

  return (
    <>
      {showCheckboxes && !negotiationMode ? (
        <OfferSelectAllBar
          selectedCount={selectedCount}
          disabled={isLoadingBidders || isEnriching || isLoadingParcels}
          onSelectAll={() => setSelectAllToken(token => token + 1)}
          onClear={() => setLocalClearToken(token => token + 1)}
        />
      ) : null}
      {parcelItems.map(parcel => (
        <ParcelOfferCard
          key={parcel.id || parcel.deliveryUnit}
          parcel={parcel}
          procurement={procurement}
          bidders={bidders}
          isLoadingBidders={isLoadingBidders}
          deliveryDetails={deliveryDetails}
          isLoadingDeliveryDetails={isEnriching}
          onViewDetail={handleViewOfferDetail}
          improvementOnly={improvementOnly}
          negotiationMode={negotiationMode}
          showCheckboxes={showCheckboxes}
          focusRequirementIndex={focusRequirementIndex}
          usesDyedUndyed={usesDyedUndyed}
          alphaColumnLabel={alphaColumnLabel}
          pricingUnit={pricingUnit}
          onRequirementSelectionChange={handleRequirementSelectionChange}
          selectionClearToken={mergedClearToken}
          selectAllToken={selectAllToken}
          alphaImprovementEditable={alphaImprovementEditable}
          onAlphaImprovementChange={onAlphaImprovementChange}
          onNegotiationOfferAvailability={onNegotiationOfferAvailability}
          preselectedOffers={preselectedOffers}
          showImprovementHpsColumns={showImprovementHpsColumns}
          isImprovementClosed={isImprovementClosed}
        />
      ))}
      {!improvementOnly && (
        <ModalOffer
          isOpen={isDetailModalOpen}
          toggle={toggleDetailModal}
          bidder={selectedOffer?.bidder}
          procurement={procurement}
        />
      )}
    </>
  );
};

export default OfferTableNonLpg;
